import React from 'react';
import { CTAButton } from './CTAButton';
import { motion } from 'framer-motion';
import { Check, Heart, Zap, ShieldCheck } from 'lucide-react';
export function Hero() {
  const scrollToSystem = () => {
    const element = document.getElementById('system');
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  return <section className="relative pt-0 pb-0 md:pt-0 px-0 bg-cream overflow-hidden" data-id="element-289">
      {/* Premium Offer Banner */}
      <motion.div initial={{
      opacity: 0,
      y: -10
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.8,
      ease: [0.25, 0.1, 0.25, 1]
    }} className="relative w-full py-3.5 px-4 text-center overflow-hidden" style={{
      background: 'linear-gradient(135deg, rgba(51,40,32,0.97) 0%, rgba(61,50,41,0.92) 50%, rgba(51,40,32,0.97) 100%)',
      backdropFilter: 'blur(16px)'
    }} data-id="element-290">
        {/* Warm glow accents */}
        <div className="absolute top-0 left-1/4 w-1/2 h-full pointer-events-none opacity-30" style={{
        background: 'radial-gradient(ellipse at center, rgba(198,114,76,0.15) 0%, transparent 70%)'
      }} data-id="element-291" />
        {/* Subtle shimmer */}
        {/* Subtle shimmer — desktop only for performance */}
        <motion.div animate={{
        x: ['-100%', '200%']
      }} transition={{
        duration: 5,
        repeat: Infinity,
        ease: 'linear',
        repeatDelay: 4
      }} className="absolute top-0 left-0 w-1/4 h-full pointer-events-none hidden md:block" style={{
        background: 'linear-gradient(90deg, transparent, rgba(250,247,242,0.06), transparent)'
      }} data-id="element-292" />
        {/* Top decorative line */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-24 md:w-40 h-px bg-gradient-to-r from-transparent via-terracotta/30 to-transparent" data-id="element-293" />
        {/* Bottom decorative line */}
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-24 md:w-40 h-px bg-gradient-to-r from-transparent via-terracotta/30 to-transparent" data-id="element-294" />

        <div className="relative flex items-center justify-center gap-3 md:gap-4 flex-wrap" data-id="element-295">
          <motion.span animate={{
          opacity: [0.5, 1, 0.5]
        }} transition={{
          duration: 2.5,
          repeat: Infinity,
          ease: 'easeInOut'
        }} className="text-terracotta/80 text-[10px] md:text-xs uppercase tracking-[0.25em] font-semibold" data-id="element-296">
            ✦ Today only ✦
          </motion.span>
          <span className="w-px h-4 bg-cream/10 hidden md:block" data-id="element-297" />
          <span className="text-cream/90 text-xs md:text-sm font-medium tracking-wide" data-id="element-298">
            Your calm starts at{' '}
            <span className="font-serif font-bold text-transparent bg-clip-text bg-gradient-to-r from-terracotta to-[#d4926a] text-sm md:text-base" data-id="element-299">
              65% off
            </span>
          </span>
          <span className="w-px h-4 bg-cream/10 hidden md:block" data-id="element-300" />
          <span className="text-cream/35 text-[10px] md:text-xs italic font-serif tracking-wide" data-id="element-301">
            Because you deserve 30 minutes, mama
          </span>
        </div>
      </motion.div>

      {/* Hero content */}
      <div className="relative pt-10 pb-0 md:pt-14 px-6 md:px-12" data-id="element-302">
        {/* Subtle ambient glow — desktop only for performance */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-terracotta/3 rounded-full blur-[120px] pointer-events-none hidden md:block" data-id="element-303" />
        <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-sage/3 rounded-full blur-[120px] pointer-events-none hidden md:block" data-id="element-304" />

        <div className="max-w-4xl mx-auto relative z-10" data-id="element-305">
          {/* Badge + Headline */}
          <motion.div initial={{
          opacity: 0,
          y: -16
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          ease: [0.25, 0.1, 0.25, 1]
        }} className="flex flex-col items-center text-center mb-8" data-id="element-306">
            <div className="flex items-center gap-2.5 mb-5 text-[11px] text-brown/40 uppercase tracking-[0.25em] font-medium" data-id="element-307">
              <div className="w-5 h-px bg-brown/15" data-id="element-308" />
              For Newborn Parents · 0–3 Months
              <div className="w-5 h-px bg-brown/15" data-id="element-309" />
            </div>

            <h1 className="font-serif text-brown leading-[1.05] mb-0" data-id="element-310">
              <motion.span initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.7,
              delay: 0.2,
              ease: [0.25, 0.1, 0.25, 1]
            }} className="block text-4xl md:text-5xl lg:text-6xl font-medium tracking-[-0.01em]" data-id="element-311">
                You're Not Lazy.
              </motion.span>
              <motion.span initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.7,
              delay: 0.45,
              ease: [0.25, 0.1, 0.25, 1]
            }} className="block text-5xl md:text-6xl lg:text-7xl italic text-terracotta font-medium mt-1" style={{
              textShadow: '0 2px 20px rgba(198,114,76,0.12)'
            }} data-id="element-312">
                You're Nap-Trapped.
              </motion.span>
            </h1>
          </motion.div>

          {/* Hero Image — immediately after headline */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.9,
          ease: [0.25, 0.1, 0.25, 1],
          delay: 0.15
        }} className="w-full mb-6" data-id="element-313">
            <div className="relative w-full" data-id="element-314">
              {/* Ambient glow behind image — desktop only */}
              <div className="absolute inset-0 -m-8 md:-m-12 bg-terracotta/5 rounded-full blur-3xl pointer-events-none hidden md:block" data-id="element-315" />
              <div className="absolute inset-0 -m-6 md:-m-8 bg-sage/4 rounded-full blur-2xl pointer-events-none hidden md:block" data-id="element-316" />

              {/* Image container — CSS float on desktop, static on mobile */}
              <div className="relative w-full rounded-2xl md:rounded-3xl overflow-hidden md:animate-[hero-float_4s_ease-in-out_infinite] will-change-transform" style={{
              aspectRatio: '4/3',
              boxShadow: '0 20px 60px rgba(196,133,106,0.18), 0 8px 24px rgba(196,133,106,0.12), 0 0 40px rgba(196,133,106,0.06)'
            }} data-id="element-317">
                <img src="https://cdn.magicpatterns.com/uploads/9kfBDM9XGTP6kXkwGVeVWH/kndd_hero_thenap2.png" alt="Mother holding her sleeping newborn in a calm, sunlit room" className="w-full h-full object-cover" loading="eager" decoding="async" fetchPriority="high" style={{
                objectPosition: 'center 50%'
              }} data-id="element-318" />
              </div>
            </div>
          </motion.div>

          {/* Copy section — starts with subtitle */}
          <motion.div initial={{
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          ease: [0.25, 0.1, 0.25, 1],
          delay: 0.3
        }} className="flex flex-col items-center text-center pb-8 md:pb-12" data-id="element-319">
            {/* Promise + emotional quote — one flowing block */}
            <p className="text-brown-light text-base md:text-lg leading-relaxed max-w-sm mb-2" data-id="element-320">
              Get 30 protected minutes back. In 7 days. Without cry-it-out.
            </p>
            <p className="font-serif text-base md:text-lg italic text-brown/55 max-w-sm mb-6" data-id="element-321">
              "You don't need perfect naps — you just need 30 minutes back."
            </p>

            {/* Compact bullets — horizontal on md, tight vertical on mobile */}
            <div className="flex flex-col md:flex-row items-center gap-2 md:gap-4 mb-7 text-[13px] text-brown-light/80" data-id="element-322">
              <span className="flex items-center gap-1.5" data-id="element-323">
                <Check className="w-3.5 h-3.5 text-sage-dark/70" strokeWidth={2.5} data-id="element-324" />
                Simple daily structure
              </span>
              <span className="hidden md:block text-brown/15" data-id="element-325">·</span>
              <span className="flex items-center gap-1.5" data-id="element-326">
                <Check className="w-3.5 h-3.5 text-sage-dark/70" strokeWidth={2.5} data-id="element-327" />
                Gentle crib steps, no tears
              </span>
              <span className="hidden md:block text-brown/15" data-id="element-328">·</span>
              <span className="flex items-center gap-1.5" data-id="element-329">
                <Check className="w-3.5 h-3.5 text-sage-dark/70" strokeWidth={2.5} data-id="element-330" />
                One hand, 3am friendly
              </span>
            </div>

            {/* CTA */}
            <CTAButton text="Yes — Give Me My 30 Minutes" className="w-full md:w-auto min-w-[300px] mb-3" data-id="element-331" />

            {/* Byline + trust — single compact block */}
            <p className="mt-3 mb-4 flex items-center justify-center gap-1.5" data-id="element-332">
              <svg className="w-3 h-3 text-terracotta/40" viewBox="0 0 24 24" fill="currentColor" data-id="element-333">
                <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" data-id="element-334" />
              </svg>
              <span className="font-serif text-xs italic text-brown/50 tracking-wide" data-id="element-335">
                Created by a mother who lived this
              </span>
            </p>
            <div className="flex items-center justify-center gap-4 sm:gap-5 text-[11px] text-brown-light/50 tracking-wide mt-1" data-id="element-336">
              <span className="inline-flex items-center gap-1.5" data-id="element-337">
                <svg className="w-3.5 h-3.5 text-sage-dark/45" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" data-id="element-338">
                  <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" data-id="element-339" />
                </svg>
                No cry-it-out
              </span>
              <span className="text-terracotta/20 text-[8px]" data-id="element-340">✦</span>
              <span className="inline-flex items-center gap-1.5" data-id="element-341">
                <svg className="w-3.5 h-3.5 text-terracotta/45" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" data-id="element-342">
                  <circle cx="12" cy="12" r="10" data-id="element-343" />
                  <polyline points="12 6 12 12 16 14" data-id="element-344" />
                </svg>
                Instant access
              </span>
              <span className="text-terracotta/20 text-[8px]" data-id="element-345">✦</span>
              <span className="inline-flex items-center gap-1.5" data-id="element-346">
                <svg className="w-3.5 h-3.5 text-sage-dark/45" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" data-id="element-347">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" data-id="element-348" />
                </svg>
                14-day guarantee
              </span>
            </div>

            <button onClick={scrollToSystem} className="mt-5 text-xs text-brown-light/40 hover:text-brown-light/70 transition-all duration-200 underline underline-offset-4 decoration-brown-light/15 hover:decoration-brown-light/35" data-id="element-349">
              See how it works ↓
            </button>
          </motion.div>
        </div>
      </div>
    </section>;
}